import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Star, Download, ChevronDown } from 'lucide-react';
import FancyCardModal from './components/FancyCardModal.tsx';
import './App.css';
import Button from './components/ui/Button.tsx';
import { useAuth } from './context/AuthContext.tsx';
import { loadRemoteCollection, saveRemoteCollection, mergeCollections } from './lib/collectionSync.ts';
import { getSetFromCache, putSetInCache } from './lib/cache.ts';
import VirtualizedGrid from './components/VirtualizedGrid.tsx';
import ImportWizard from './components/ImportWizard.tsx';
import { updateSetCompletion } from './lib/completion.ts';

function App() {
  const [cards, setCards] = useState([]);
  const [ownedCards, setOwnedCards] = useState(() => {
    const saved = localStorage.getItem('ownedCards');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [wantedCards, setWantedCards] = useState(() => {
    const saved = localStorage.getItem('wantedCards');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [selectedSet, setSelectedSet] = useState(() => {
    const saved = localStorage.getItem('selectedSet');
    return saved ? JSON.parse(saved) : 'all';
  });
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(18);
  const [selectedCard, setSelectedCard] = useState(null);
  const [viewMode, setViewMode] = useState('main'); // 'main' | 'master'
  const { user } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [gridCols, setGridCols] = useState(() => Number(localStorage.getItem('gridCols')) || 4);

  // Load set index quickly for dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/cards/index.json');
        const idx = await res.json();
        if (!cancelled) setSetsIndex(idx);
      } catch (err) {
        console.error('Failed to load set index:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    localStorage.setItem('ownedCards', JSON.stringify([...ownedCards]));
  }, [ownedCards]);

  useEffect(() => {
    localStorage.setItem('wantedCards', JSON.stringify([...wantedCards]));
  }, [wantedCards]);

  // Sync from Firestore on login
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      try {
        const remote = await loadRemoteCollection(user.uid);
        if (!remote) return;
        if (cancelled) return;
        const merged = mergeCollections(ownedCards, wantedCards, remote.owned, remote.wanted);
        setOwnedCards(merged.owned);
        setWantedCards(merged.wanted);
      } catch (e) {
        console.error('Failed to load remote collection', e);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Debounced remote save on changes (when logged in)
  useEffect(() => {
    if (!user) return;
    const handle = setTimeout(() => {
      saveRemoteCollection(user.uid, ownedCards, wantedCards).catch((e) =>
        console.error('Failed to save remote collection', e)
      );
    }, 600);
    return () => clearTimeout(handle);
  }, [user, ownedCards, wantedCards]);

  useEffect(() => {
    localStorage.setItem('selectedSet', JSON.stringify(selectedSet));
  }, [selectedSet]);

  const makeKey = (card) => (card && card.variant ? `${card.id}|${card.variant}` : card.id);

  const getCardStatus = (card) => {
    const key = makeKey(card);
    if (ownedCards.has(key)) return 'owned';
    if (wantedCards.has(key)) return 'wanted';
    return 'missing';
  };

  const [setsIndex, setSetsIndex] = useState([]);

  const sets = useMemo(() => ['all', ...setsIndex.map((s) => s.name)], [setsIndex]);

  // Load only the selected set (or all if 'all')
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (selectedSet === 'all') {
          // Load a few popular sets by default to show something; or keep empty until user selects
          setCards([]);
          return;
        }
        const found = setsIndex.find((s) => s.name === selectedSet);
        if (!found) return;
        const cached = await getSetFromCache(found.slug);
        if (cached && !cancelled) setCards(cached);
        const fresh = await fetch(`/cards/${found.slug}.json`).then((r) => r.json());
        if (!cancelled) {
          setCards(fresh);
          putSetInCache(found.slug, fresh).catch(() => {});
        }
      } catch (e) {
        console.error('Failed to load set', e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSet, setsIndex]);

  const filteredBySet = selectedSet === 'all' 
    ? cards 
    : cards.filter(card => card.set.name === selectedSet);

  // Derive available finish variants per card from pricing fields
  const getVariants = (card) => {
    const variants = new Set();
    const tp = card.tcgplayer?.prices || {};
    if (tp.normal) variants.add('normal');
    if (tp.holofoil) variants.add('holofoil');
    if (tp.reverseHolofoil) variants.add('reverseHolofoil');
    if (tp['1stEdition']) variants.add('1stEdition');
    if (tp['1stEditionHolofoil']) variants.add('1stEditionHolofoil');

    const cm = card.cardmarket?.prices || {};
    if (!variants.size && (cm.averageSellPrice || cm.lowPrice || cm.trendPrice)) {
      variants.add('normal');
    }
    if (cm.reverseHoloTrend || cm.reverseHoloLow || cm.reverseHoloSell) {
      variants.add('reverseHolofoil');
    }
    if (!variants.size) variants.add('normal');
    return Array.from(variants);
  };

  const expandedByView = viewMode === 'master'
    ? filteredBySet.flatMap((c) => getVariants(c).map((v) => ({ ...c, variant: v })))
    : filteredBySet;

  const filteredCards = ownershipFilter === 'all'
    ? expandedByView
    : expandedByView.filter(card => getCardStatus(card) === ownershipFilter);

  const cardsOnPage = filteredCards.slice(0, visibleCount);

  const handleSetStatus = (card, newStatus, variant) => {
    const key = variant ? `${card.id}|${variant}` : card.id;
    const newOwned = new Set(ownedCards);
    const newWanted = new Set(wantedCards);

    if (newStatus === 'owned') {
      newOwned.add(key);
      newWanted.delete(key);
    } else if (newStatus === 'wanted') {
      newWanted.add(key);
      newOwned.delete(key);
    } else {
      // missing
      newOwned.delete(key);
      newWanted.delete(key);
    }
    setOwnedCards(newOwned);
    setWantedCards(newWanted);
  };

  const handleOpenDetail = (card) => setSelectedCard(card);
  const handleCloseDetail = () => setSelectedCard(null);

  const handleExportWanted = () => {
    const wantedCardsList = cards.filter(card => wantedCards.has(card.id));
    const header = "Cards you are missing\n\n";
    const rows = wantedCardsList.map(card => `${card.set.name} - ${card.number} - ${card.name}`).join('\n');
    const exportData = header + rows;
    const blob = new Blob([exportData], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wanted_cards.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 18);
  };
  
  const totalInSet = selectedSet === 'all' ? 0 : filteredBySet.length;
  const ownedInSet = selectedSet === 'all' ? 0 : filteredBySet.filter(card => ownedCards.has(card.id)).length;
  const missingInSet = totalInSet - ownedInSet;

  useEffect(() => {
    if (!user) return;
    if (selectedSet === 'all') return;
    if (!totalInSet) return;
    const complete = ownedInSet >= totalInSet && totalInSet > 0;
    updateSetCompletion(user.uid, selectedSet, complete).catch(() => {});
  }, [user, selectedSet, totalInSet, ownedInSet]);

  return (
    <div className="max-w-[1100px] mx-auto my-5 p-5 bg-gray-800/50 rounded-xl shadow-2xl">
      <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-5 py-3 -mx-5 -mt-5 mb-5 z-50 rounded-t-xl">
        <div className="flex flex-wrap justify-center gap-5 items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="set-filter" className="text-sm text-gray-400">Filter by set:</label>
            <div className="relative">
              <select
                id="set-filter"
                value={selectedSet}
                onChange={e => {
                  setSelectedSet(e.target.value);
                  setVisibleCount(18);
                }}
                className="appearance-none bg-gray-800 border border-gray-700 rounded-lg py-2 pl-4 pr-10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              >
                {sets.map(set => (
                  <option key={set} value={set}>{set}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label htmlFor="ownership-filter" className="text-sm text-gray-400">Show:</label>
            <div className="relative">
              <select
                id="ownership-filter"
                value={ownershipFilter}
                onChange={e => {
                  setOwnershipFilter(e.target.value)
                  setVisibleCount(18);
                }}
                className="appearance-none bg-gray-800 border border-gray-700 rounded-lg py-2 pl-4 pr-10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              >
                <option value="all">All</option>
                <option value="owned">Owned</option>
                <option value="wanted">Wanted</option>
                <option value="missing">Missing</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <Button variant="success" size="md" onClick={handleExportWanted} leftIcon={Download}>
            Export Wanted List
          </Button>
        </div>
        {selectedSet !== 'all' && (
          <div className="text-white text-center pt-4">
            <h3 className="text-lg font-semibold mb-1">{selectedSet}</h3>
            <div className="flex justify-center gap-4 text-sm">
              <p><span className="text-status-owned font-medium">Owned:</span> {ownedInSet} / {totalInSet} ({Math.round((ownedInSet/totalInSet)*100) || 0}%)</p>
              <p><span className="text-status-missing font-medium">Missing:</span> {missingInSet}</p>
            </div>
          </div>
        )}
      </div>
      <div className="bg-gray-950 p-5 rounded-xl">
        <div className="flex flex-wrap justify-center gap-3 items-center mb-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setViewMode((m) => (m === 'main' ? 'master' : 'main'));
              setVisibleCount(18);
            }}
          >
            {viewMode === 'main' ? 'Master Set View' : 'Main Set View'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>Import</Button>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400">Cards per row:</label>
            <div className="relative">
              <select
                value={gridCols}
                onChange={(e)=>{
                  const n = Math.min(4, Math.max(2, parseInt(e.target.value, 10) || 4));
                  localStorage.setItem('gridCols', String(n));
                  setGridCols(n);
                }}
                className="appearance-none bg-gray-800 border border-gray-700 rounded-lg py-1 pl-3 pr-8 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
              >
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>
        <div className="relative">
          <VirtualizedGrid
            items={cardsOnPage}
            columnWidth={220}
            rowHeight={310}
            gap={20}
            columnsOverride={gridCols}
            render={(card) => {
              const status = getCardStatus(card);
              return (
                <div
                  key={card.variant ? `${card.id}|${card.variant}` : card.id}
                  className={`bg-gray-800 rounded-xl relative aspect-[2.5/3.5] flex justify-center items-center overflow-hidden cursor-pointer transition-transform hover:scale-105 ${status === 'missing' ? 'opacity-70' : ''}`}
                  onClick={() => handleOpenDetail(card)}
                >
                  {status === 'owned' && (
                    <div className="absolute top-2 right-2 z-10 text-status-owned drop-shadow-lg">
                      <CheckCircle className="w-7 h-7" />
                    </div>
                  )}
                  {status === 'wanted' && (
                    <div className="absolute top-2 right-2 z-10 text-status-wanted drop-shadow-lg">
                      <Star className="w-7 h-7" />
                    </div>
                  )}
                  <img src={card.images.small} alt={card.name} className="max-w-full max-h-full block" />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/75 text-gray-200 p-2 translate-y-full transition-transform duration-300 text-xs pointer-events-none group-hover:translate-y-0 hover:translate-y-0 [.card-slot:hover_&]:translate-y-0">
                    <p className="font-medium truncate">{card.name}</p>
                    <p className="text-gray-400 truncate">Set: {card.set.name}</p>
                    <p className="text-gray-400">ID: {card.id}</p>
                    {card.variant && <p className="text-gray-400">Variant: {card.variant}</p>}
                    {status === 'missing' && (
                      <Button
                        variant="primary"
                        size="sm"
                        className="mt-2 w-full pointer-events-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (card.cardmarket && card.cardmarket.url) {
                            window.open(card.cardmarket.url, '_blank');
                          } else {
                            const cardName = encodeURIComponent(card.name);
                            const setName = encodeURIComponent(card.set.name);
                            window.open(`https://www.cardmarket.com/en/Pokemon/Products/Singles?searchString=${cardName}+${setName}`, '_blank');
                          }
                        }}
                      >
                        Search on CardMarket
                      </Button>
                    )}
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
      {/* Mobile bottom action bar */}
      <div className="mobile-action-bar">
        <select
          value={selectedSet}
          onChange={e => {
            setSelectedSet(e.target.value);
            setVisibleCount(18);
          }}
          className="appearance-none bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white text-sm"
        >
          {sets.map(set => (
            <option key={set} value={set}>{set}</option>
          ))}
        </select>
        <select
          value={ownershipFilter}
          onChange={e => {
            setOwnershipFilter(e.target.value)
            setVisibleCount(18);
          }}
          className="appearance-none bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-white text-sm"
        >
          <option value="all">All</option>
          <option value="owned">Owned</option>
          <option value="wanted">Wanted</option>
          <option value="missing">Missing</option>
        </select>
        <Button variant="success" size="sm" onClick={handleExportWanted}>Export</Button>
      </div>
      {visibleCount < filteredCards.length && (
        <div className="text-center mt-5">
          <Button variant="success" size="lg" onClick={handleLoadMore}>Load More</Button>
        </div>
      )}

      {selectedCard && (
        <FancyCardModal
          card={selectedCard}
          variant={selectedCard.variant}
          status={getCardStatus(selectedCard)}
          onClose={handleCloseDetail}
          onSetStatus={(cardArg, statusArg) => handleSetStatus(cardArg, statusArg, selectedCard.variant)}
        />
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onApply={(entries) => {
          const nextOwned = new Set(ownedCards);
          const nextWanted = new Set(wantedCards);
          for (const e of entries) {
            if (e.status === 'owned') { nextOwned.add(e.key); nextWanted.delete(e.key); }
            else if (e.status === 'wanted') { nextWanted.add(e.key); nextOwned.delete(e.key); }
            else { nextOwned.delete(e.key); nextWanted.delete(e.key); }
          }
          setOwnedCards(nextOwned);
          setWantedCards(nextWanted);
          setImportOpen(false);
        }}
      />
    </div>
  );
}

export default App; 